import { EDITORIAL_REVIEWER_UI_READY } from 'app/config/editorialReviewer.config';
import { canSendEditorialRequest } from 'app/services/editorialRequest.service';
import { ANALYTICS_EVENT, trackEventBeforeUnload } from 'app/config/analytics.config';
// Constantes puras: importar app/store/index aqui crea un ciclo que rompe el arranque.
import { PROGRAM_ACCESS_DENIED_ERROR_KEY, PROGRAM_ACCESS_DENIED_MESSAGE, PROGRAM_ACCESS_REVOKED_EVENT } from 'app/models/editorialProgramRoute';
import { REDUCER_SYSTEM_SETTINGS } from 'app/store/conf';
import { removeAllStorage } from 'app/utils/utils';
import axios from 'axios';

const SESSION_REVOKED_ERROR_KEY = 'SESSION_REVOKED';

/**
 * Contrato de expulsion con i2c (mitad de frontend) para la sesion unica Mint.
 * Cuando la blacklist de JWT revoca el token de una sesion backoffice cerrada,
 * i2c NO responde por SERVICE_RESPONSES.responseError sino por el handler de
 * seguridad de swagger: ensureAuth serializa un error cuyo status HTTP es 403 y
 * cuyo cuerpo lleva errorKey: 'SESSION_REVOKED' a NIVEL RAIZ
 * (response.data.errorKey, NO response.data.error.errorKey). Ese 403 lo captura
 * el manejador generico de 403 de abajo, que ya limpia la sesion y redirige a
 * /auth/login: la revocacion viaja como un 403 mas y solo se distingue del 403
 * de rol para emitir su evento de telemetria.
 *
 * El 409 ACTIVE_SESSION_EXISTS del login es un camino distinto: sale por
 * SERVICE_RESPONSES.responseError con forma { error: { errorKey } } y lo maneja
 * la LoginPage, no este interceptor.
 */

/**
 * Configura interceptores globales de axios para la aplicacion
 *
 * Request Interceptor:
 * - Inyecta token de autenticacion en headers Authorization
 * - Configura baseURL desde configuracion de entorno
 *
 * Response Interceptor:
 * - Maneja errores 403 (denegacion de rol o revocacion de sesion Mint via
 *   errorKey SESSION_REVOKED) redirigiendo a login
 * - Distingue entre requests canceladas y errores reales
 * - Retorna estructura consistente { error } para manejo de errores
 * - Retorna { error: null, cancelled: true } para cancelaciones
 *
 * @param {Object} axiosInstance - Instancia de axios
 * @param {string} baseUrl - URL base de la API
 * @param {Object} store - Redux store para acceder a authToken
 */
function guardEditorialAdapter(config, store) {
  if (!EDITORIAL_REVIEWER_UI_READY || config.editorialAction === undefined) return;
  const adapter = axios.getAdapter(config.adapter);
  config.adapter = (request) => {
    const authToken = store.getState().auth.authToken;
    if (!canSendEditorialRequest(request, authToken)) throw new axios.CanceledError('Editorial authorization changed before dispatch');
    return adapter(request);
  };
}

export function setupAxios(axiosInstance, baseUrl, store) {
  // La revocacion tumba a la vez todas las peticiones en vuelo: sin esta marca
  // una sola expulsion emitiria un evento por cada 403 en paralelo e inflaria la
  // metrica. Vive lo que la pagina, asi que la siguiente expulsion vuelve a contar.
  let sessionRevokedTracked = false;

  axiosInstance.interceptors.request.use(
    (config) => {
      const {
        auth: { authToken },
      } = store.getState();

      if (!canSendEditorialRequest(config, authToken)) throw new axios.CanceledError('Editorial authorization is not ready');
      guardEditorialAdapter(config, store);

      if (authToken) {
        config.headers.Authorization = `${authToken}`;
      }

      config.baseURL = baseUrl;
      return config;
    },
    (err) => Promise.reject(err)
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // La denegacion de programa no expira la sesion: avisa al boundary, que revalida y
      // devuelve a "Mis programas" por el router, sin recargar la pestana.
      const errorData = error.response?.data;
      const programDenied = errorData?.errorKey === PROGRAM_ACCESS_DENIED_ERROR_KEY || errorData?.message === PROGRAM_ACCESS_DENIED_MESSAGE;

      if (error.response?.status === 403 && programDenied) {
        globalThis.dispatchEvent(new Event(PROGRAM_ACCESS_REVOKED_EVENT));
        return { error: error.response };
      }

      // Un 403 (denegacion de rol o revocacion de sesion Mint via SESSION_REVOKED,
      // ver cabecera) limpia la sesion local y vuelve al login.
      if (error.response?.status === 403) {
        if (error.response?.data?.errorKey === SESSION_REVOKED_ERROR_KEY && !sessionRevokedTracked) {
          sessionRevokedTracked = true;
          trackEventBeforeUnload(ANALYTICS_EVENT.SESSION_REVOKED, { tenant: store.getState()?.[REDUCER_SYSTEM_SETTINGS]?.organization?.tenant?.guid });
        }

        removeAllStorage();
        window.location.href = '/auth/login';
        return;
      }

      if (axios.isCancel(error)) {
        return {
          error: null,
          cancelled: true,
          message: error.message || 'Request cancelled',
        };
      }

      return {
        error: error.response || error,
      };
    }
  );
}
